import * as React from "react";
import { Navigate, Route, useLocation } from "react-router-dom";
import { userInfoStore } from "Stores/UserInfoStore";
import { AdminDashboard } from "Pages/Admin/Pages/Dashboard/AdminDashboard";
import { AdminTasks } from "Pages/Admin/Pages/Tasks/AdminTasks";
import { SwitchWith404 } from "Config/Router";
import { BreadcrumbsGenerator } from "Components/General/BreadcrumbsGenerator";
import { Projects } from "./Pages/Projects/Projects";
import { LoadAsync } from "Components/General/Loadable";
import { clearAllStores } from "Stores/RESTStore/CoreStore";
import { CustomerInfo } from "./Pages/CustomerInfo/CustomerInfo";
import { EmailSignature } from "./Pages/EmailSignature";
import { ReleaseNotes } from "./Pages/ReleaseNotes/ReleaseNotes";
import classNames from "classnames";
import { ProjectGroupTemplates } from "./Pages/ProjectGroupTemplates";
import { SystemRolesOverview } from "./Pages/SystemRoles/SystemRolesOverview";
import { ExternalApplications } from "./Pages/ExternalApplications/ExternalApplications";
import { McpAdmin } from "./Pages/Mcp/McpAdmin";
import { AdminLogs } from "./Pages/AdminLogs/AdminLogs";

const Users = LoadAsync(() => import(/* webpackChunkName: "users" */ "Pages/Admin/Pages/Users/User"));
const Accounts = LoadAsync(() => import(/* webpackChunkName: "accounts" */ "Pages/Admin/Pages/Accounts/Accounts"));
// tslint:disable-next-line:max-line-length
const ProjectTemplates = LoadAsync(
  () => import(/* webpackChunkName: "prtemplates" */ "Pages/Admin/Pages/ProjectTemplates/ProjectTemplates")
);
// tslint:disable-next-line:max-line-length
const ProjectGroup = LoadAsync(
  () => import(/* webpackChunkName: "prgroup" */ "Pages/Admin/Pages/ProjectGroup/ProjectGroup")
);
// tslint:disable-next-line:max-line-length
const ProjectAccess = LoadAsync(
  () => import(/* webpackChunkName: "praccess" */ "Pages/Admin/Pages/ProjectAccess/ProjectAccess")
);
// tslint:disable-next-line:max-line-length
const SSO = LoadAsync(() => import(/* webpackChunkName: "sso" */ "Pages/Admin/Pages/SSO/SSO"));
const SystemInfo = LoadAsync(
  () => import(/* webpackChunkName: "sys-info" */ "Pages/Admin/Pages/SystemInfo/SystemInfo")
);
const EmailWhiteList = LoadAsync(
  () => import(/* webpackChunkName: "email-whlist" */ "Pages/Admin/Pages/EmailWhiteList")
);
const LockedLoginsManager = LoadAsync(
  () => import(/* webpackChunkName: "locked-logins" */ "Pages/Admin/Pages/LockedLoginsManager")
);
const ServiceProviders = LoadAsync(
  () => import(/* webpackChunkName: "service-providers" */ "Pages/Admin/Pages/ServiceProviders/ServiceProviders")
);

const OXInterfaces = LoadAsync(() => import(/* webpackChunkName: "locked-logins" */ "Pages/Admin/Pages/OXInterfaces"));

const FeatureFlags = LoadAsync(
  () => import(/* webpackChunkName: "feature-flags" */ "Pages/Admin/Pages/FeatureFlags/FeatureFlags")
);

export const Admin = () => {
  const location = useLocation();
  React.useEffect(() => {
    return () => {
      clearAllStores();
    };
  }, []);

  const adminRoutes = [
    { "/admin": "Administration" },
    { "/admin/projectgroup": "Project Groups" },
    { "/admin/user": "Users" },
    { "/admin/accounts": "Accounts" },
    { "/admin/accounts/\\d+$": "Edit account" },
    { "/admin/user/new": "New user" },
    { "/admin/user/edit/\\d+$": "Edit user" },
    { "/admin/projects": "Projects" },
    { "/admin/projects/new": "New project" },
    { "/admin/projects/edit/\\d+$": "Edit project" },
    { "/admin/service-providers": "Print Service Providers" },
    { "/admin/service-providers/new": "New Provider" },
    { "/admin/service-providers/edit/\\d+$": "Edit Provider" },
    { "/admin/email-signature": "Signature for emails from sys admins" },
    { "/admin/templates": "Project templates" },
    { "/admin/templates/new": "New project template" },
    { "/admin/templates/edit/\\d+$": "Edit project template" },
    { "/admin/projects/\\d+/filename-rules": "Filename rules" },
    { "/admin/projects/\\d+/metadata": "Metadata" },
    { "/admin/projects/\\d+/clone": "Clone project" },
    { "/admin/templates/\\d+/clone": "Clone template project" },
    { "/admin/templates/\\d+/repro-service": "Repro services" },
    { "/admin/projects/\\d+/repro-service": "Repro services" },
    { "/admin/projects/\\d+/views": "Views" },
    { "/admin/projects/\\d+/auto-mapping": `Automatic directory mapping` },
    { "/admin/tasks": "Tasks" },
    { "/admin/info": "Customer information" },
    { "/admin/releaseNotes": "Release notes" },
    { "/admin/project-access": "Approvals" },
    { "/admin/projects/\\d+/auto-organise": "Auto-Organise" },
    { "/admin/pg/\\d+/templates": "Template-Projects" },
    { "/admin/pg/\\d+/projects": "Projects" },
    { "/admin/pg/\\d+/projects/new": `New project` },
    { "/admin/pg/\\d+/user": "Users" },
    { "/admin/pg/\\d+/user/new": `New user` },
    { "/admin/pg/\\d+/user/edit/\\d+$": "Edit user" },
    { "/admin/sso": "SSO Servers" },
    { "/admin/sso/new": "New server" },
    { "/admin/sso/edit/\\d+$": "Edit server" },
    { "/admin/system-info": "System Information" },
    { "/admin/support-files": "Files in download area" },
    { "/admin/white-list": "Whitelist of mail domains" },
    { "/admin/locked-logins": "Locked logins" },
    { "/admin/ox-interfaces": "OX-interfaces" },
    { "/admin/system-roles": "System roles" },
    { "/admin/external-applications": "External Applications" },
    { "/admin/mcp": "MCP" },
    { "/admin/mcp/overview": "Overview" },
    { "/admin/mcp/quota": "Quota" },
    { "/admin/mcp/connections": "Connections" },
    { "/admin/mcp/clients": "Clients" },
    { "/admin/mcp/registrations": "Registrations" },
    { "/admin/logs": "Logs" },
    { "/admin/feature-flags": "Feature flags" },
    { "/admin/feature-flags/audit": "History" },
    { "/admin/feature-flags/\\d+/assignments": "Assignments" },
  ];

  return (
    <div className="dashboard">
      <BreadcrumbsGenerator
        config={adminRoutes}
        className={classNames({ "extended-layout-breadcrumbs": location.pathname.includes("workflows") })}
      />
      <SwitchWith404>
        {userInfoStore.isVisibleFor("ADMIN") && <Route path={`projectgroup/*`} element={<ProjectGroup />} />}
        <Route path={`info`} element={<CustomerInfo />} />
        <Route path={`releaseNotes`} element={<ReleaseNotes />} />
        <Route path={`pg/:prGroupId/user/*`} element={<Users />} />
        <Route path={`user/*`} element={<Users />} />
        <Route path={`accounts/*`} element={<Accounts />} />
        <Route path={`tasks/*`} element={<AdminTasks />} />
        <Route path={`pg/:prGroupId/projects/*`} element={<Projects />} />
        <Route path={`projects/*`} element={<Projects />} />
        <Route path={`templates/*`} element={<ProjectTemplates />} />
        <Route path={`pg/:projectGroupId/templates`} element={<ProjectGroupTemplates />} />

        <Route path={`email-signature`} element={<EmailSignature />} />
        <Route path={`sso/*`} element={<SSO />} />
        <Route path={`project-access`}>
          <Route path=":taskId" element={<ProjectAccess />} />
          <Route path="" element={<ProjectAccess />} />
        </Route>
        <Route path={`system-info`} element={<SystemInfo />} />
        <Route path={`white-list`} element={<EmailWhiteList />} />
        <Route path={`locked-logins`} element={<LockedLoginsManager />} />
        <Route path={`ox-interfaces`} element={<OXInterfaces />} />
        <Route path={`system-roles`} element={<SystemRolesOverview />} />
        <Route path={`external-applications`} element={<ExternalApplications />} />
        <Route path={`mcp-quota`} element={<Navigate to="/admin/mcp/quota" replace />} />
        <Route path={`mcp/:tab?`} element={<McpAdmin />} />
        <Route path={`logs`} element={<AdminLogs />} />
        {userInfoStore.isSysAdmin && <Route path={`feature-flags/*`} element={<FeatureFlags />} />}
        <Route path={`service-providers/*`} element={<ServiceProviders />} />

        <Route index element={<AdminDashboard />} />
      </SwitchWith404>
    </div>
  );
};
